import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  BALL_DEFS, VARIANT_BALL, RARITY_BALL_DEFAULT, STANDARD_BALL_KEYS,
} from "../lib/balls";

// Les 10 paliers de rareté connus (lib/rarity.js tierOf) — ordre et libellés
// décidés avec l'utilisateur le 2026-10-03, pika/promo/ace volontairement
// regroupés ("tout ce qui est un peu étrange").
const KNOWN_TIERS = [
  { key: "double", label: "Double rare (ex)" },
  { key: "ultra", label: "Ultra rare (V / VMAX / VSTAR / GX...)" },
  { key: "ir", label: "Illustration rare (AR)" },
  { key: "sir", label: "Illustration spéciale rare (SAR)" },
  { key: "hyper", label: "Hyper rare / Rainbow / Gold" },
  { key: "secret", label: "Secret rare" },
  { key: "shiny", label: "Chromatique / brillant / radieux" },
  { key: "pika", label: "Promo Pikachu" },
  { key: "promo", label: "Promo générique" },
  { key: "ace", label: "ACE SPEC" },
];

// Panneau Réglages : associe chaque palier de rareté (+ d'éventuelles
// raretés "vraiment spéciales" ajoutées à la main) à une Poké Ball — les
// valeurs par défaut sont codées dans lib/balls.js, cette table ne porte
// que ce que l'utilisateur a explicitement changé (PUT/DELETE
// /api/rarity-balls, réservé aux admins côté backend).
export const RarityBallSettings = ({ rarityBalls = {}, onSetRarityBall, onClearRarityBall }) => {
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newBall, setNewBall] = useState("");
  const [saveErr, setSaveErr] = useState(null);

  // Clés custom = tout ce qui est en base mais n'est ni un palier connu ni
  // une clé réservée aux variantes standard.
  const knownKeys = new Set(KNOWN_TIERS.map(t => t.key));
  const customKeys = Object.keys(rarityBalls).filter(
    k => !knownKeys.has(k) && !STANDARD_BALL_KEYS.has(k)
  );

  const rows = [
    ...KNOWN_TIERS.map(t => ({ key: t.key, label: t.label, custom: false })),
    ...customKeys.map(k => ({ key: k, label: k, custom: true })),
  ];

  // Ball actuellement affichée pour une ligne : override explicite sinon
  // valeur par défaut du code (absente pour une rareté custom tant qu'elle
  // n'a pas encore de ball choisie). rarityBalls[row.key] est une clé
  // (string, ex. "safari") renvoyée par l'API — à résoudre en objet
  // {src,name} via BALL_DEFS, jamais utilisée telle quelle.
  const currentBall = (row) => {
    const overrideKey = rarityBalls[row.key];
    if (overrideKey && BALL_DEFS[overrideKey]) return BALL_DEFS[overrideKey];
    return RARITY_BALL_DEFAULT[row.key] || null;
  };

  // Balls déjà prises ailleurs (variantes standard + toutes les autres
  // lignes) : jamais proposées dans le menu d'une ligne, pour qu'une ball
  // ne porte jamais deux significations à la fois.
  const takenElsewhere = (exceptKey) => {
    const taken = new Set(STANDARD_BALL_KEYS);
    for (const row of rows) {
      if (row.key === exceptKey) continue;
      const b = currentBall(row);
      if (b) taken.add(Object.keys(BALL_DEFS).find(k => BALL_DEFS[k] === b) || null);
    }
    return taken;
  };

  const availableFor = (row) => {
    const taken = takenElsewhere(row.key);
    return Object.keys(BALL_DEFS).filter(k => !taken.has(k));
  };

  const handlePick = (row, ballKey) => {
    if (!ballKey) { onClearRarityBall?.(row.key); return; }
    onSetRarityBall?.(row.key, ballKey);
  };

  const submitNew = async () => {
    setSaveErr(null);
    const key = newKey.trim();
    if (!key || !newBall) return;
    try {
      await onSetRarityBall?.(key, newBall);
      setAdding(false); setNewKey(""); setNewBall("");
    } catch (err) {
      setSaveErr("Échec — réservé aux administrateurs ?");
    }
  };

  // Balls libres pour une TOUTE NOUVELLE ligne (rien n'est encore pris par
  // elle-même, contrairement à availableFor ci-dessus).
  const freeForNewRow = Object.keys(BALL_DEFS).filter(k => !takenElsewhere(null).has(k));

  return (
    <div>
      <div className="space-y-1.5">
        {rows.map(row => {
          const ball = currentBall(row);
          const ballKey = ball ? Object.keys(BALL_DEFS).find(k => BALL_DEFS[k] === ball) : "";
          const options = availableFor(row);
          return (
            <div key={row.key}
              className="flex items-center justify-between gap-3 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2">
              <span className="text-sm text-zinc-200 flex items-center gap-2">
                {row.label}
                {row.custom && (
                  <span className="text-[10px] uppercase tracking-wide text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">
                    perso
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  value={ballKey || ""}
                  onChange={(e) => handlePick(row, e.target.value)}
                  className="bg-zinc-900 border border-zinc-700 rounded-md text-sm text-zinc-200 px-2 py-1.5 max-w-[200px]"
                >
                  {!ballKey && <option value="">— choisir —</option>}
                  {ballKey && <option value={ballKey}>{BALL_DEFS[ballKey].name}</option>}
                  {options.filter(k => k !== ballKey).map(k => (
                    <option key={k} value={k}>{BALL_DEFS[k].name}</option>
                  ))}
                </select>
                {ball && <img src={ball.src} alt="" width={28} height={28} />}
                {row.custom && (
                  <button onClick={() => onClearRarityBall?.(row.key)}
                    title="Retirer cette rareté"
                    className="text-zinc-500 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {adding ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-2.5">
          <Input value={newKey} onChange={(e) => setNewKey(e.target.value)}
            placeholder='Texte exact de la rareté (ex. "HIGH-TECG rare")'
            className="bg-zinc-900 border-zinc-700 text-sm flex-1 min-w-[180px]" />
          <select value={newBall} onChange={(e) => setNewBall(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-md text-sm text-zinc-200 px-2 py-1.5">
            <option value="">— ball —</option>
            {freeForNewRow.map(k => (
              <option key={k} value={k}>{BALL_DEFS[k].name}</option>
            ))}
          </select>
          <Button size="sm" onClick={submitNew} disabled={!newKey.trim() || !newBall}
            className="bg-red-600 hover:bg-red-500 text-white">Ajouter</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setSaveErr(null); }}
            className="text-zinc-400 hover:text-white">Annuler</Button>
          {saveErr && <p className="text-xs text-red-400 w-full">{saveErr}</p>}
        </div>
      ) : (
        <Button onClick={() => setAdding(true)}
          className="mt-3 bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
          <Plus className="w-4 h-4 mr-2" />Ajouter une rareté vraiment spéciale
        </Button>
      )}

      <div className="mt-4 pt-3 border-t border-zinc-800">
        <p className="text-xs text-zinc-500 mb-2">Variantes standard (non modifiable ici) :</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(VARIANT_BALL).map(([type, def]) => (
            <span key={type} className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <img src={def.src} alt="" width={18} height={18} />{def.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
